import { Message } from '@twilio/conversations';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import { AvatarAccent, DrawerTitle, InitialsAvatar, Tag, Text } from '@growtherapy/sprout-ui';

import { DrawerHeaderWrapper as DrawerHeader } from '../../components/DrawerHeaderWrapper';
import { ParticipantStatus } from '../../types';
import { nameToInitials } from '../../utils';

export type ParticipantConversation = {
  conversationSid: string;
  status: ParticipantStatus;
  chatName: string;
  hasUnreadMessages: boolean;
  onOpenConversation: () => void;
  lastMessage?: Message;
};

export function AllClientsChatDrawer({ activeChats }: { activeChats: ParticipantConversation[] }) {
  return (
    <>
      <DrawerHeader>
        <DrawerTitle>Chat</DrawerTitle>
      </DrawerHeader>
      <ul>
        {activeChats.map((chat) => {
          const { conversationSid, status, chatName, hasUnreadMessages, onOpenConversation, lastMessage } = chat;
          const ariaLabel = `participant(s) ${status.toLowerCase()}`;
          return (
            <li key={conversationSid}>
              <button
                className='flex items-center px-3 py-5 border-neutral-400 border-b w-full hover:bg-neutral-200 rebrand:bg-surface-inverseEmphasis rebrand:hover:bg-surface-inverseEmphasis'
                onClick={onOpenConversation}
                aria-label={hasUnreadMessages ? 'Has unread messages' : ''}
              >
                <div
                  className={twMerge(
                    classNames('h-2 w-2 min-w-[0.5rem] rounded-full mr-2', {
                      'bg-lilac-800': hasUnreadMessages,
                    }),
                  )}
                />
                <InitialsAvatar
                  aria-hidden
                  accent={AvatarAccent.Lilac}
                  size='sm'
                  initials={nameToInitials(chatName.split(',')[0])}
                  className='min-w-[3rem] mr-2 flex justify-center'
                />
                <div className='w-full'>
                  <div className='flex w-full'>
                    <Text
                      as='span'
                      className='font-medium text-ellipsis overflow-hidden whitespace-nowrap max-w-[calc(100%-10rem)] fs-exclude rebrand:text-text-inverse'
                      data-dd-privacy='mask'
                      data-dd-action-name='Click on chat name in all clients drawer'
                    >
                      {chatName}
                    </Text>
                    <Tag
                      className='ml-1'
                      aria-label={ariaLabel}
                      use={status === ParticipantStatus.IN_SESSION ? 'green' : 'neutral'}
                    >
                      {status}
                    </Tag>
                  </div>
                  <div className='text-left text-neutral-700'>
                    {lastMessage ? (
                      <Text
                        as='span'
                        variant='sm'
                        className='font-semibold text-ellipsis overflow-hidden whitespace-nowrap max-w-[calc(100%-8rem)] fs-exclude rebrand:text-text-inverse'
                        data-dd-privacy='mask'
                        data-dd-action-name='Click on message preview in all clients drawer'
                      >
                        {lastMessage.body}
                      </Text>
                    ) : (
                      <Text variant='sm' className='rebrand:text-text-inverse'>
                        Write a new message
                      </Text>
                    )}
                    {lastMessage && (
                      <Text variant='xs' className='rebrand:text-text-inverse'>
                        {lastMessage.dateCreated?.toLocaleTimeString([], {
                          hour: 'numeric',
                          minute: 'numeric',
                        })}
                      </Text>
                    )}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}
